import { useEffect, useState } from "react"

import { useTestSetMetadataTransport } from "@/test-set/transport"
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { AlertCircle } from "lucide-react"

import type { JsonObject } from "@/types/json"

type StringParameterMap = Record<string, string>

interface TemplateParamsSectionProps {
  testSetId?: string
  iterationCount: number | null
  testCaseParams: JsonObject[]
  onParamsChange: (params: JsonObject[]) => void
  readOnly?: boolean
}

export function TemplateParamsSection({
  testSetId,
  iterationCount,
  testCaseParams,
  onParamsChange,
  readOnly = false,
}: TemplateParamsSectionProps) {
  const [localParams, setLocalParams] = useState<StringParameterMap[]>(
    (testCaseParams as StringParameterMap[]) || [],
  )
  const [paramKeys, setParamKeys] = useState<string[]>([])
  const [paramOptions, setParamOptions] = useState<Record<string, string[]>>({})
  const [useFirstForAll, setUseFirstForAll] = useState(false)

  const { data: testSetData } = useTestSetMetadataTransport(testSetId || "", {
    enabled: !!testSetId,
  })

  // Get available parameter keys and options from the test set
  useEffect(() => {
    if (testSetData?.metadata?.parameters) {
      const keys = Object.keys(testSetData.metadata.parameters)
      setParamKeys(keys)
      setParamOptions(
        testSetData.metadata.parameters as Record<string, string[]>,
      )
    } else {
      setParamKeys([])
      setParamOptions({})
    }
  }, [testSetData])

  // Initialize or resize params array when iteration count changes
  useEffect(() => {
    if (iterationCount === null) {
      return
    }

    // If there are no params yet, create empty ones
    if (!localParams.length) {
      const initialParams = Array(iterationCount)
        .fill({})
        .map(() => ({}))
      setLocalParams(initialParams)
      return
    }

    // If iteration count changed, resize the array
    if (localParams.length !== iterationCount) {
      let newParams: StringParameterMap[]

      if (localParams.length < iterationCount) {
        // Add new empty params
        const extraParams = Array(iterationCount - localParams.length)
          .fill({})
          .map(() => ({}))
        newParams = [...localParams, ...extraParams]
      } else {
        // Truncate params
        newParams = localParams.slice(0, iterationCount)
      }

      setLocalParams(newParams)
      onParamsChange(newParams)
    }
  }, [iterationCount, localParams, onParamsChange])

  // Update params when testCaseParams prop changes
  useEffect(() => {
    if (
      testCaseParams &&
      testCaseParams.length > 0 &&
      JSON.stringify(testCaseParams) !== JSON.stringify(localParams)
    ) {
      setLocalParams(testCaseParams as StringParameterMap[])
    }
  }, [testCaseParams, localParams])

  // Handle changes to a parameter value
  const handleParamChange = (
    iterationIndex: number,
    paramKey: string,
    value: string,
  ) => {
    if (readOnly) {
      return
    }

    // Create a new array with a copy of each object
    let updatedParams = localParams.map((param, i) =>
      i === iterationIndex ? { ...param, [paramKey]: value } : { ...param },
    )

    // If useFirstForAll is checked and we're changing the first iteration,
    // apply the same value to all iterations
    if (useFirstForAll && iterationIndex === 0) {
      updatedParams = updatedParams.map((param) => ({
        ...param,
        [paramKey]: value,
      }))
    }

    // If the iteration doesn't exist yet, add it
    if (iterationIndex >= updatedParams.length) {
      updatedParams[iterationIndex] = { [paramKey]: value }
    }

    // Ensure all params are proper objects with string values
    const sanitizedParams = updatedParams.map((param) => {
      const result: JsonObject = {}
      Object.keys(param).forEach((key) => {
        // Only include non-empty values
        if (param[key]) {
          result[key] = String(param[key])
        }
      })
      return result
    })

    setLocalParams(sanitizedParams as StringParameterMap[])
    onParamsChange(sanitizedParams)
  }

  // Handle applying first iteration values to all iterations
  const handleUseFirstForAll = () => {
    if (localParams.length > 0 && iterationCount) {
      const firstParams = localParams[0] || {}
      const updatedParams = Array(iterationCount)
        .fill(null)
        .map(() => ({ ...firstParams }))

      setLocalParams(updatedParams)
      onParamsChange(updatedParams)
      setUseFirstForAll(true)
    }
  }

  // Handle randomizing values for all iterations
  const handleRandomize = () => {
    if (iterationCount && paramKeys.length > 0) {
      const randomizedParams: StringParameterMap[] = []

      for (let i = 0; i < iterationCount; i++) {
        const paramObject: StringParameterMap = {}

        paramKeys.forEach((key) => {
          const options = paramOptions[key] || []
          if (options.length > 0) {
            const randomIndex = Math.floor(Math.random() * options.length)
            paramObject[key] = options[randomIndex]
          }
        })

        randomizedParams.push(paramObject)
      }

      setLocalParams(randomizedParams)
      onParamsChange(randomizedParams)
      setUseFirstForAll(false)
    }
  }

  // If no test set is selected or test set has no parameters
  if (!testSetId || !paramKeys.length) {
    return null
  }

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold mb-4 text-coval-text">
              Test Case Parameters
            </h3>
            {iterationCount && iterationCount > 0 && !readOnly && (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleUseFirstForAll}
                  disabled={readOnly}
                >
                  Use First For All
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleRandomize}
                  disabled={readOnly}
                >
                  Randomize
                </Button>
              </div>
            )}
          </div>
          <div className="h-px bg-coval-border mb-4" />
          {!iterationCount || iterationCount <= 0 ? (
            <div className="flex items-start gap-2 p-4 rounded-lg bg-coval-warning-surface border border-coval-warning-border mb-4">
              <AlertCircle className="h-4 w-4 text-coval-warning-on-surface mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-coval-warning-on-surface mb-1">
                  Please set the number of iterations first
                </div>
                <div className="text-sm text-coval-warning-on-surface">
                  Parameters are configured per iteration
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <colgroup>
                    <col className="w-24" />
                    {paramKeys.map((key) => (
                      <col key={`col-${key}`} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="text-left p-2 border-b border-coval-border w-24">
                        Iteration
                      </th>
                      {paramKeys.map((key) => (
                        <th
                          key={key}
                          className="text-left p-2 border-b border-coval-border"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: iterationCount || 0 }).map(
                      (_, index) => (
                        <tr
                          key={index}
                          className="border-b border-coval-border"
                        >
                          <td className="p-2 text-muted-foreground">
                            {index + 1}
                          </td>
                          {paramKeys.map((key) => (
                            <td key={`${index}-${key}`} className="p-2">
                              {paramOptions[key] ? (
                                <Select
                                  value={localParams[index]?.[key] ?? ""}
                                  onValueChange={(value) => {
                                    handleParamChange(index, key, value)
                                  }}
                                  disabled={readOnly}
                                >
                                  <SelectTrigger
                                    aria-label={`Select ${key}`}
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={`Select ${key}`}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {paramOptions[key].map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : null}
                            </td>
                          ))}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
